import React, { useState, useEffect } from 'react';
import { newtonProtocolsAPI } from '../api';
import { useTheme } from '../theme/ThemeProvider';
import DataSourceList from './newton-protocols/DataSourceList';
import DataSourceModal from './newton-protocols/DataSourceModal';
import ProtocolList from './newton-protocols/ProtocolList';
import ProtocolEditor from './newton-protocols/ProtocolEditor';
import TemplateGallery from './newton-protocols/TemplateGallery';
import ProtocolViewer from './newton-protocols/ProtocolViewer';

const NewtonProtocols = () => {
  const { mode } = useTheme();
  
  // Estados
  const [dataSources, setDataSources] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('protocols'); // 'protocols' | 'templates'
  
  // Estado de modales
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  
  // Estado de protocolos
  const [showProtocolEditor, setShowProtocolEditor] = useState(false);
  const [viewingProtocol, setViewingProtocol] = useState(null);
  const [editingProtocol, setEditingProtocol] = useState(null);
  
  // Cargar datos al montar
  useEffect(() => {
    loadDataSources();
    loadProtocols();
    loadTemplates();
  }, []);
  
  // Auto-ocultar mensajes después de 5 segundos
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  // Funciones de carga de datos
  const loadDataSources = async () => {
    try {
      setLoading(true);
      const response = await newtonProtocolsAPI.dataSources.getAll();
      if (response.success) {
        setDataSources(response.data);
      }
    } catch (err) {
      console.error('Error loading data sources:', err);
      setError('Error cargando fuentes de datos');
    } finally {
      setLoading(false);
    }
  };
  
  const loadProtocols = async () => {
    try {
      const response = await newtonProtocolsAPI.protocols.getAll();
      if (response.success) {
        setProtocols(response.data);
      }
    } catch (err) {
      console.error('Error loading protocols:', err);
      setError('Error cargando protocolos');
    }
  };
  
  const loadTemplates = async () => {
    try {
      const response = await newtonProtocolsAPI.protocols.getTemplates();
      if (response.success) {
        setTemplates(response.data);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      // No mostrar error para templates, pueden no existir aún
    }
  };
  
  // Handlers de fuentes
  const handleCreateSource = () => {
    setEditingSource(null);
    setShowSourceModal(true);
  };
  
  const handleEditSource = (source) => {
    setEditingSource(source);
    setShowSourceModal(true);
  };
  
  const handleSyncSources = async () => {
    if (!window.confirm('¿Sincronizar fuentes de datos automáticamente? El sistema escaneará la base de datos y actualizará el catálogo.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await newtonProtocolsAPI.dataSources.sync();
      if (response.success) {
        setSuccess(`✅ ${response.message} (Creadas: ${response.stats.created}, Actualizadas: ${response.stats.updated}, Desactivadas: ${response.stats.deactivated})`);
        loadDataSources();
      }
    } catch (err) {
      console.error('Error sincronizando fuentes:', err);
      setError(err.response?.data?.detail || 'Error sincronizando fuentes');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta fuente de datos?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await newtonProtocolsAPI.dataSources.delete(sourceId);
      if (response.success) {
        setSuccess('Fuente eliminada exitosamente');
        loadDataSources();
      }
    } catch (err) {
      console.error('Error deleting source:', err);
      setError(err.response?.data?.detail || 'Error eliminando fuente');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveSource = async (sourceData) => {
    try {
      setLoading(true);
      let response;
      
      if (editingSource) {
        response = await newtonProtocolsAPI.dataSources.update(editingSource.id, sourceData);
      } else {
        response = await newtonProtocolsAPI.dataSources.create(sourceData);
      }
      
      if (response.success) {
        setSuccess(response.message || 'Fuente guardada exitosamente');
        setShowSourceModal(false);
        loadDataSources();
      }
    } catch (err) {
      console.error('Error saving source:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Handlers de protocolos
  const handleCreateProtocol = () => {
    setEditingProtocol(null);
    setShowProtocolEditor(true);
  };
  
  const handleViewProtocol = (protocol) => {
    setViewingProtocol(protocol);
  };
  
  const handleEditProtocol = (protocol) => {
    setEditingProtocol(protocol);
    setViewingProtocol(null);
    setShowProtocolEditor(true);
  };
  
  const handleDeleteProtocol = async (protocolId) => {
    if (!window.confirm('¿Estás seguro de eliminar este protocolo?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await newtonProtocolsAPI.protocols.delete(protocolId);
      if (response.success) {
        setSuccess('Protocolo eliminado exitosamente');
        loadProtocols();
      }
    } catch (err) {
      console.error('Error deleting protocol:', err);
      setError(err.response?.data?.detail || 'Error eliminando protocolo');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDuplicateProtocol = async (protocolId) => {
    try {
      setLoading(true);
      const response = await newtonProtocolsAPI.protocols.duplicate(protocolId);
      if (response.success) {
        setSuccess(response.message || 'Protocolo duplicado exitosamente');
        loadProtocols();
      }
    } catch (err) {
      console.error('Error duplicating protocol:', err);
      setError(err.response?.data?.detail || 'Error duplicando protocolo');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUseTemplate = async (templateId) => {
    try {
      setLoading(true);
      const response = await newtonProtocolsAPI.protocols.duplicate(templateId);
      if (response.success) {
        setSuccess('¡Plantilla copiada exitosamente! Ahora puedes personalizarla.');
        setActiveTab('protocols'); // Cambiar a pestaña de protocolos
        loadProtocols();
      }
    } catch (err) {
      console.error('Error using template:', err);
      setError(err.response?.data?.detail || 'Error usando plantilla');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveProtocol = async (protocolData) => {
    try {
      setLoading(true);
      let response;
      
      if (editingProtocol) {
        response = await newtonProtocolsAPI.protocols.update(editingProtocol.id, protocolData);
      } else {
        response = await newtonProtocolsAPI.protocols.create(protocolData);
      }
      
      if (response.success) {
        setSuccess(response.message || 'Protocolo guardado exitosamente');
        setShowProtocolEditor(false);
        loadProtocols();
      }
    } catch (err) {
      console.error('Error saving protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Estilos
  const styles = {
    container: {
      padding: 'var(--spacing-xl)',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: 'var(--spacing-2xl)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      paddingBottom: 'var(--spacing-lg)'
    },
    title: {
      fontSize: 'var(--font-size-4xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginBottom: 'var(--spacing-md)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-md)'
    },
    subtitle: {
      fontSize: 'var(--font-size-lg)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      marginBottom: 0
    },
    section: {
      marginBottom: 'var(--spacing-3xl)'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 'var(--spacing-lg)'
    },
    sectionTitle: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-sm)'
    },
    notification: {
      padding: 'var(--spacing-md) var(--spacing-lg)',
      borderRadius: 'var(--border-radius-lg)',
      marginBottom: 'var(--spacing-lg)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-md)',
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-medium)',
      animation: 'slideInDown 0.3s ease-out'
    },
    successNotification: {
      backgroundColor: mode === 'dark' ? 'rgba(56, 193, 114, 0.15)' : 'rgba(56, 193, 114, 0.1)',
      color: '#38C172',
      border: '1px solid #38C172'
    },
    errorNotification: {
      backgroundColor: mode === 'dark' ? 'rgba(255, 107, 53, 0.15)' : 'rgba(255, 107, 53, 0.1)',
      color: '#FF6B35',
      border: '1px solid #FF6B35'
    },
    emptyState: {
      textAlign: 'center',
      padding: 'var(--spacing-3xl)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderRadius: 'var(--border-radius-lg)',
      border: `2px dashed ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`
    },
    emptyStateIcon: {
      fontSize: 'var(--font-size-6xl)',
      marginBottom: 'var(--spacing-md)',
      opacity: 0.5
    },
    emptyStateText: {
      fontSize: 'var(--font-size-lg)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      marginBottom: 'var(--spacing-lg)'
    }
  };
  
  // Si está viendo un protocolo, mostrar el viewer
  if (viewingProtocol) {
    return (
      <ProtocolViewer
        protocol={viewingProtocol}
        dataSources={dataSources}
        onBack={() => setViewingProtocol(null)}
        onEdit={() => {
          setEditingProtocol(viewingProtocol);
          setViewingProtocol(null);
          setShowProtocolEditor(true);
        }}
      />
    );
  }
  
  // Si está en modo editor de protocolo, mostrar solo el editor
  if (showProtocolEditor) {
    return (
      <ProtocolEditor
        protocol={editingProtocol}
        dataSources={dataSources}
        onSave={handleSaveProtocol}
        onCancel={() => setShowProtocolEditor(false)}
        loading={loading}
      />
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <span>⚡</span>
          Protocolos de Newton
        </h1>
        <p style={styles.subtitle}>
          Gestiona tus fuentes de datos y crea visualizaciones personalizadas
        </p>
      </div>
      
      {/* Notificaciones */}
      {success && (
        <div style={{...styles.notification, ...styles.successNotification}}>
          <span>✓</span>
          {success}
        </div>
      )}
      
      {error && (
        <div style={{...styles.notification, ...styles.errorNotification}}>
          <span>⚠</span>
          {error}
        </div>
      )}
      
      {/* Sección de Fuentes de Datos */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            <span>🔌</span>
            Fuentes de Datos
          </h2>
          <div style={{display: 'flex', gap: 'var(--spacing-sm)'}}>
            <button 
              className="btn btn-secondary"
              onClick={handleSyncSources}
              disabled={loading}
              title="Sincronizar fuentes automáticamente desde la base de datos"
            >
              🔄 Sincronizar
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleCreateSource}
              disabled={loading}
            >
              + Añadir Fuente
            </button>
          </div>
        </div>
        
        {dataSources.length > 0 ? (
          <DataSourceList
            sources={dataSources}
            onEdit={handleEditSource}
            onDelete={handleDeleteSource}
            loading={loading}
          />
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>🔌</div>
            <p style={styles.emptyStateText}>
              No hay fuentes de datos configuradas
            </p>
            <button 
              className="btn btn-primary"
              onClick={handleCreateSource}
            >
              Crear Primera Fuente
            </button>
          </div>
        )}
      </div>
      
      {/* Sección de Protocolos con Tabs */}
      <div style={styles.section}>
        {/* Header con Tabs */}
        <div style={styles.sectionHeader}>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center' }}>
            <h2 style={styles.sectionTitle}>
              <span>📊</span>
              Protocolos
            </h2>
            <div style={{
              display: 'flex',
              gap: 'var(--spacing-xs)',
              backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
              borderRadius: 'var(--border-radius-md)',
              padding: 'var(--spacing-xs)'
            }}>
              <button
                onClick={() => setActiveTab('templates')}
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--border-radius-sm)',
                  border: 'none',
                  backgroundColor: activeTab === 'templates' ? '#29B5E8' : 'transparent',
                  color: activeTab === 'templates' ? '#FFFFFF' : mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                🎨 Plantillas ({templates.length})
              </button>
              <button
                onClick={() => setActiveTab('protocols')}
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderRadius: 'var(--border-radius-sm)',
                  border: 'none',
                  backgroundColor: activeTab === 'protocols' ? '#29B5E8' : 'transparent',
                  color: activeTab === 'protocols' ? '#FFFFFF' : mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📋 Mis Protocolos ({protocols.length})
              </button>
            </div>
          </div>
          {activeTab === 'protocols' && (
            <button 
              className="btn btn-primary"
              onClick={handleCreateProtocol}
              disabled={loading}
            >
              + Crear Protocolo
            </button>
          )}
        </div>
        
        {/* Contenido según tab activa */}
        {activeTab === 'templates' ? (
          <TemplateGallery
            templates={templates}
            onUseTemplate={handleUseTemplate}
            loading={loading}
          />
        ) : (
          <>
            {protocols.length > 0 ? (
              <ProtocolList
                protocols={protocols}
                onView={handleViewProtocol}
                onEdit={handleEditProtocol}
                onDelete={handleDeleteProtocol}
                onDuplicate={handleDuplicateProtocol}
                loading={loading}
              />
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyStateIcon}>📊</div>
                <p style={styles.emptyStateText}>
                  No hay protocolos creados
                </p>
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateProtocol}
                >
                  Crear Primer Protocolo
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Modal de Fuente de Datos */}
      {showSourceModal && (
        <DataSourceModal
          source={editingSource}
          onSave={handleSaveSource}
          onCancel={() => setShowSourceModal(false)}
          loading={loading}
        />
      )}
    </div>
  );
};

export default NewtonProtocols;

